/** @module @liquidefy/code-editor/adapters/adapter-monaco @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  Position,
  Range,
  Selection,
  CompletionKind,
  CompletionItem,
  InsertTextRule,
  DiagnosticSeverity,
  DiagnosticInfo,
  EditorConfig,
  DiffEditorState,
  CursorStyle,
  WordWrapMode,
  CommandData,
  CommandId,
  KeybindingData,
  Token,
  TokenType,
  SemanticToken,
  MonacoCommandMapping,
  EditorEventData,
  EditorEventDomain,
  LanguageId,
  EventChannelId,
} from '../code-editor-types.js';

import {
  ok,
  err,
  now,
  asCommandId,
  asEventChannelId,
} from '../code-editor.js';

// ═══ §2 POSITION & RANGE MAPPING — bidirectional position/range/selection conversion ═══

export interface MonacoPosition {
  readonly lineNumber: number;
  readonly column: number;
}

export interface MonacoRange {
  readonly startLineNumber: number;
  readonly startColumn: number;
  readonly endLineNumber: number;
  readonly endColumn: number;
}

export interface MonacoSelection {
  readonly selectionStartLineNumber: number;
  readonly selectionStartColumn: number;
  readonly positionLineNumber: number;
  readonly positionColumn: number;
}

export function positionToMonaco(position: Position): MonacoPosition {
  return {
    lineNumber: position.line + 1,
    column: position.column + 1,
  };
}

export function positionFromMonaco(monacoPos: MonacoPosition): Position {
  return {
    line: monacoPos.lineNumber - 1,
    column: monacoPos.column - 1,
  };
}

export function rangeToMonaco(range: Range): MonacoRange {
  return {
    startLineNumber: range.start.line + 1,
    startColumn: range.start.column + 1,
    endLineNumber: range.end.line + 1,
    endColumn: range.end.column + 1,
  };
}

export function rangeFromMonaco(monacoRange: MonacoRange): Range {
  return {
    start: {
      line: monacoRange.startLineNumber - 1,
      column: monacoRange.startColumn - 1,
    },
    end: {
      line: monacoRange.endLineNumber - 1,
      column: monacoRange.endColumn - 1,
    },
  };
}

export function selectionToMonaco(selection: Selection): MonacoSelection {
  return {
    selectionStartLineNumber: selection.anchor.line + 1,
    selectionStartColumn: selection.anchor.column + 1,
    positionLineNumber: selection.active.line + 1,
    positionColumn: selection.active.column + 1,
  };
}

export function selectionFromMonaco(monacoSelection: MonacoSelection): Selection {
  const anchor: Position = {
    line: monacoSelection.selectionStartLineNumber - 1,
    column: monacoSelection.selectionStartColumn - 1,
  };
  const active: Position = {
    line: monacoSelection.positionLineNumber - 1,
    column: monacoSelection.positionColumn - 1,
  };
  const isReversed =
    anchor.line > active.line ||
    (anchor.line === active.line && anchor.column > active.column);
  return { anchor, active, isReversed };
}

// ═══ §3 COMPLETION MAPPING — completion items and kinds bidirectional ═══

export const COMPLETION_KIND_MAP: ReadonlyMap<CompletionKind, number> = new Map([
  ['text', 18],
  ['method', 0],
  ['function', 1],
  ['constructor', 2],
  ['field', 3],
  ['variable', 4],
  ['class', 5],
  ['interface', 7],
  ['module', 8],
  ['property', 9],
  ['unit', 12],
  ['value', 13],
  ['enum', 15],
  ['keyword', 17],
  ['snippet', 27],
  ['color', 19],
  ['file', 20],
  ['reference', 21],
  ['folder', 23],
  ['enum-member', 16],
  ['constant', 14],
  ['struct', 6],
  ['event', 10],
  ['operator', 11],
  ['type-parameter', 24],
]);

const MONACO_COMPLETION_KIND_REVERSE: ReadonlyMap<number, CompletionKind> = new Map(
  Array.from(COMPLETION_KIND_MAP.entries()).map(([k, v]) => [v, k]),
);

export function completionKindToMonaco(kind: CompletionKind): number {
  return COMPLETION_KIND_MAP.get(kind) ?? 18;
}

export function completionKindFromMonaco(monacoKind: number): CompletionKind {
  return MONACO_COMPLETION_KIND_REVERSE.get(monacoKind) ?? 'text';
}

export function insertTextRulesToMonaco(rules?: InsertTextRule[]): number {
  if (!rules || rules.length === 0) return 0;
  let flags = 0;
  for (const rule of rules) {
    if (rule === 'keep-whitespace') flags |= 1;
    if (rule === 'insert-as-snippet') flags |= 4;
  }
  return flags;
}

export function completionItemToMonaco(item: CompletionItem): Record<string, unknown> {
  return {
    label: item.label,
    kind: completionKindToMonaco(item.kind),
    detail: item.detail ?? '',
    documentation: item.documentation ?? '',
    insertText: item.insertText,
    insertTextRules: insertTextRulesToMonaco(item.insertTextRules),
    range: rangeToMonaco(item.range),
    sortText: item.sortText ?? item.label,
    filterText: item.filterText ?? item.label,
    preselect: item.preselect ?? false,
    commitCharacters: item.commitCharacters ?? [],
    additionalTextEdits: (item.additionalTextEdits ?? []).map(edit => ({
      range: rangeToMonaco(edit.range),
      text: edit.text,
    })),
    command: item.command ? commandToMonaco(item.command) : undefined,
  };
}

export function completionItemFromMonaco(monacoItem: Record<string, unknown>): Partial<CompletionItem> {
  const range = monacoItem['range'] as MonacoRange | undefined;
  return {
    label: monacoItem['label'] as string,
    kind: completionKindFromMonaco(monacoItem['kind'] as number),
    detail: monacoItem['detail'] as string | undefined,
    documentation: monacoItem['documentation'] as string | undefined,
    insertText: monacoItem['insertText'] as string,
    sortText: monacoItem['sortText'] as string | undefined,
    filterText: monacoItem['filterText'] as string | undefined,
    preselect: monacoItem['preselect'] as boolean | undefined,
    range: range ? rangeFromMonaco(range) : { start: { line: 0, column: 0 }, end: { line: 0, column: 0 } },
  };
}

export function completionListToMonaco(items: CompletionItem[]): Record<string, unknown>[] {
  return items.map(completionItemToMonaco);
}

// ═══ §4 DIAGNOSTIC MAPPING — diagnostics, severity, model markers ═══

export const SEVERITY_MAP: ReadonlyMap<DiagnosticSeverity, number> = new Map([
  ['error', 8],
  ['warning', 4],
  ['info', 2],
  ['hint', 1],
]);

const SEVERITY_REVERSE: ReadonlyMap<number, DiagnosticSeverity> = new Map([
  [8, 'error'],
  [4, 'warning'],
  [2, 'info'],
  [1, 'hint'],
]);

export function severityToMonaco(severity: DiagnosticSeverity): number {
  return SEVERITY_MAP.get(severity) ?? 2;
}

export function severityFromMonaco(monacoSeverity: number): DiagnosticSeverity {
  return SEVERITY_REVERSE.get(monacoSeverity) ?? 'info';
}

export function diagnosticToMonaco(diagnostic: DiagnosticInfo): Record<string, unknown> {
  return {
    severity: severityToMonaco(diagnostic.severity),
    message: diagnostic.message,
    startLineNumber: diagnostic.range.start.line + 1,
    startColumn: diagnostic.range.start.column + 1,
    endLineNumber: diagnostic.range.end.line + 1,
    endColumn: diagnostic.range.end.column + 1,
    source: diagnostic.source ?? '',
    code: diagnostic.code,
    relatedInformation: (diagnostic.relatedInformation ?? []).map(ri => ({
      resource: { path: ri.uri },
      message: ri.message,
      startLineNumber: ri.range.start.line + 1,
      startColumn: ri.range.start.column + 1,
      endLineNumber: ri.range.end.line + 1,
      endColumn: ri.range.end.column + 1,
    })),
    tags: (diagnostic.tags ?? []).map(tag =>
      tag === 'unnecessary' ? 1 : tag === 'deprecated' ? 2 : 0,
    ),
  };
}

export function diagnosticFromMonaco(monacoMarker: Record<string, unknown>): DiagnosticInfo {
  const tags = (monacoMarker['tags'] as number[] | undefined) ?? [];
  return {
    severity: severityFromMonaco(monacoMarker['severity'] as number),
    message: monacoMarker['message'] as string,
    range: {
      start: {
        line: (monacoMarker['startLineNumber'] as number) - 1,
        column: (monacoMarker['startColumn'] as number) - 1,
      },
      end: {
        line: (monacoMarker['endLineNumber'] as number) - 1,
        column: (monacoMarker['endColumn'] as number) - 1,
      },
    },
    source: monacoMarker['source'] as string | undefined,
    code: monacoMarker['code'] as string | number | undefined,
    tags: tags
      .map(t => (t === 1 ? ('unnecessary' as const) : t === 2 ? ('deprecated' as const) : null))
      .filter((t): t is 'unnecessary' | 'deprecated' => t !== null),
  };
}

export function diagnosticsToModelMarkers(
  diagnostics: DiagnosticInfo[],
  uri: string,
): Record<string, unknown>[] {
  return diagnostics.map(d => ({
    ...diagnosticToMonaco(d),
    resource: { path: uri },
  }));
}

// ═══ §5 EDITOR OPTIONS — EditorConfig ↔ Monaco IEditorOptions bidirectional ═══

export function wordWrapToMonaco(mode: WordWrapMode): string {
  switch (mode) {
    case 'off': return 'off';
    case 'on': return 'on';
    case 'wordWrap': return 'wordWrapColumn';
    case 'bounded': return 'bounded';
  }
}

export function cursorStyleToMonaco(style: CursorStyle): string {
  switch (style) {
    case 'block': return 'block';
    case 'underline': return 'underline';
    case 'bar': return 'line';
    case 'block-outline': return 'block-outline';
    case 'underline-thin': return 'underline-thin';
    case 'bar-thin': return 'line-thin';
  }
}

export function lineNumbersToMonaco(mode: 'on' | 'off' | 'relative' | 'interval'): string {
  switch (mode) {
    case 'on': return 'on';
    case 'off': return 'off';
    case 'relative': return 'relative';
    case 'interval': return 'interval';
  }
}

export function themeToMonaco(theme: string): string {
  switch (theme) {
    case 'dark': return 'vs-dark';
    case 'light': return 'vs';
    case 'high-contrast-dark': return 'hc-black';
    case 'high-contrast-light': return 'hc-light';
    default: return theme;
  }
}

export function editorConfigToMonacoOptions(config: EditorConfig): Record<string, unknown> {
  return {
    tabSize: config.tabSize,
    insertSpaces: config.insertSpaces,
    wordWrap: wordWrapToMonaco(config.wordWrap.mode),
    wordWrapColumn: config.wordWrap.column ?? 80,
    minimap: {
      enabled: config.minimap.enabled,
      side: config.minimap.side,
      showSlider: config.minimap.showSlider,
      renderCharacters: config.minimap.renderCharacters,
      maxColumn: config.minimap.maxColumn,
      scale: config.minimap.scale,
    },
    lineNumbers: lineNumbersToMonaco(config.gutter.lineNumbers),
    folding: config.gutter.folding,
    glyphMargin: config.gutter.glyphMargin,
    lineDecorationsWidth: config.gutter.lineDecorationsWidth,
    smoothScrolling: config.scroll.smoothScrolling,
    scrollBeyondLastLine: config.scroll.scrollBeyondLastLine,
    scrollBeyondLastColumn: config.scroll.scrollBeyondLastColumn,
    fastScrollSensitivity: config.scroll.fastScrollSensitivity,
    mouseWheelScrollSensitivity: config.scroll.mouseWheelScrollSensitivity,
    stickyScroll: {
      enabled: config.stickyScroll.enabled,
      maxLineCount: config.stickyScroll.maxLineCount,
      defaultModel: config.stickyScroll.defaultModel,
    },
    cursorStyle: cursorStyleToMonaco(config.cursorStyle),
    cursorBlinking: config.cursorBlinking,
    lineHeight: config.lineHeight,
    fontSize: config.fontSize,
    fontFamily: config.fontFamily,
    fontWeight: config.fontWeight,
    fontLigatures: config.fontLigatures,
    renderWhitespace: config.renderWhitespace,
    renderControlCharacters: config.renderControlCharacters,
    renderLineHighlight: config.renderLineHighlight,
    matchBrackets: config.matchBrackets,
    autoClosingBrackets: config.autoClosingBrackets,
    autoClosingQuotes: config.autoClosingQuotes,
    autoSurround: config.autoSurround,
    autoIndent: config.autoIndent,
    dragAndDrop: config.dragAndDrop,
    emptySelectionClipboard: config.emptySelectionClipboard,
    linkedEditing: config.linkedEditing,
    occurrencesHighlight: config.occurrencesHighlight ? 'singleFile' : 'off',
    showFoldingControls: config.showFoldingControls,
    unfoldOnClickAfterEndOfLine: config.unfoldOnClickAfterEndOfLine,
    guides: {
      bracketPairs: config.guides.bracketPairs,
      bracketPairsHorizontal: config.guides.bracketPairsHorizontal,
      highlightActiveBracketPair: config.guides.highlightActiveBracketPair,
      indentation: config.guides.indentation,
      highlightActiveIndentation: config.guides.highlightActiveIndentation,
    },
    formatOnType: false,
    formatOnPaste: false,
  };
}

export function monacoOptionsToEditorConfig(options: Record<string, unknown>): Partial<EditorConfig> {
  const result: Record<string, unknown> = {};

  if (options['tabSize'] !== undefined) result['tabSize'] = options['tabSize'];
  if (options['insertSpaces'] !== undefined) result['insertSpaces'] = options['insertSpaces'];
  if (options['fontSize'] !== undefined) result['fontSize'] = options['fontSize'];
  if (options['fontFamily'] !== undefined) result['fontFamily'] = options['fontFamily'];
  if (options['fontWeight'] !== undefined) result['fontWeight'] = options['fontWeight'];
  if (options['fontLigatures'] !== undefined) result['fontLigatures'] = options['fontLigatures'];
  if (options['lineHeight'] !== undefined) result['lineHeight'] = options['lineHeight'];
  if (options['cursorBlinking'] !== undefined) result['cursorBlinking'] = options['cursorBlinking'];
  if (options['renderWhitespace'] !== undefined) result['renderWhitespace'] = options['renderWhitespace'];
  if (options['renderControlCharacters'] !== undefined) result['renderControlCharacters'] = options['renderControlCharacters'];
  if (options['renderLineHighlight'] !== undefined) result['renderLineHighlight'] = options['renderLineHighlight'];
  if (options['matchBrackets'] !== undefined) result['matchBrackets'] = options['matchBrackets'];
  if (options['autoClosingBrackets'] !== undefined) result['autoClosingBrackets'] = options['autoClosingBrackets'];
  if (options['autoClosingQuotes'] !== undefined) result['autoClosingQuotes'] = options['autoClosingQuotes'];
  if (options['autoSurround'] !== undefined) result['autoSurround'] = options['autoSurround'];
  if (options['autoIndent'] !== undefined) result['autoIndent'] = options['autoIndent'];
  if (options['dragAndDrop'] !== undefined) result['dragAndDrop'] = options['dragAndDrop'];
  if (options['linkedEditing'] !== undefined) result['linkedEditing'] = options['linkedEditing'];

  if (typeof options['cursorStyle'] === 'string') {
    const cursorMap: Record<string, CursorStyle> = {
      'block': 'block', 'underline': 'underline', 'line': 'bar',
      'block-outline': 'block-outline', 'underline-thin': 'underline-thin', 'line-thin': 'bar-thin',
    };
    result['cursorStyle'] = cursorMap[options['cursorStyle'] as string] ?? 'bar';
  }

  if (typeof options['wordWrap'] === 'string') {
    const wrapMap: Record<string, WordWrapMode> = {
      'off': 'off', 'on': 'on', 'wordWrapColumn': 'wordWrap', 'bounded': 'bounded',
    };
    result['wordWrap'] = {
      mode: wrapMap[options['wordWrap'] as string] ?? 'off',
      column: options['wordWrapColumn'] as number | undefined,
    };
  }

  return result as Partial<EditorConfig>;
}

export function diffOptionsToMonaco(diffState: DiffEditorState): Record<string, unknown> {
  return {
    renderSideBySide: diffState.renderSideBySide,
    ignoreTrimWhitespace: diffState.ignoreTrimWhitespace,
    maxComputationTime: diffState.maxComputationTime,
    renderIndicators: true,
    enableSplitViewResizing: true,
  };
}

// ═══ §6 COMMAND MAPPING — commands, keybindings, registry ═══

export const BUILTIN_COMMAND_MAP: ReadonlyMap<string, string> = new Map([
  ['editor.action.formatDocument', 'editor.action.formatDocument'],
  ['editor.action.formatSelection', 'editor.action.formatSelection'],
  ['editor.action.quickFix', 'editor.action.quickFix'],
  ['editor.action.rename', 'editor.action.rename'],
  ['editor.action.goToDeclaration', 'editor.action.revealDefinition'],
  ['editor.action.goToTypeDefinition', 'editor.action.goToTypeDefinition'],
  ['editor.action.goToImplementation', 'editor.action.goToImplementation'],
  ['editor.action.findReferences', 'editor.action.referenceSearch.trigger'],
  ['editor.action.commentLine', 'editor.action.commentLine'],
  ['editor.action.blockComment', 'editor.action.blockComment'],
  ['editor.action.indentLines', 'editor.action.indentLines'],
  ['editor.action.outdentLines', 'editor.action.outdentLines'],
  ['editor.action.moveLinesUp', 'editor.action.moveLinesUpAction'],
  ['editor.action.moveLinesDown', 'editor.action.moveLinesDownAction'],
  ['editor.action.copyLinesUp', 'editor.action.copyLinesUpAction'],
  ['editor.action.copyLinesDown', 'editor.action.copyLinesDownAction'],
  ['editor.action.deleteLines', 'editor.action.deleteLines'],
  ['editor.action.toggleWordWrap', 'editor.action.toggleWordWrap'],
  ['editor.action.triggerSuggest', 'editor.action.triggerSuggest'],
  ['editor.action.triggerParameterHints', 'editor.action.triggerParameterHints'],
  ['editor.fold', 'editor.fold'],
  ['editor.unfold', 'editor.unfold'],
  ['editor.foldAll', 'editor.foldAll'],
  ['editor.unfoldAll', 'editor.unfoldAll'],
  ['editor.action.selectAll', 'editor.action.selectAll'],
  ['editor.action.clipboardCut', 'editor.action.clipboardCutAction'],
  ['editor.action.clipboardCopy', 'editor.action.clipboardCopyAction'],
  ['editor.action.clipboardPaste', 'editor.action.clipboardPasteAction'],
  ['undo', 'undo'],
  ['redo', 'redo'],
]);

export function commandToMonaco(command: CommandData): Record<string, unknown> {
  const monacoId = BUILTIN_COMMAND_MAP.get(command.id) ?? command.id;
  return {
    id: monacoId,
    title: command.title,
  };
}

export function commandFromMonaco(monacoCommand: Record<string, unknown>): CommandData {
  const monacoId = monacoCommand['id'] as string;
  let editorCommandId = monacoId;
  for (const [k, v] of BUILTIN_COMMAND_MAP) {
    if (v === monacoId) {
      editorCommandId = k;
      break;
    }
  }
  return {
    id: asCommandId(editorCommandId),
    title: (monacoCommand['title'] as string) ?? monacoId,
  };
}

const MODIFIER_MAP: Record<string, string> = {
  'ctrl': 'Ctrl',
  'shift': 'Shift',
  'alt': 'Alt',
  'meta': 'Meta',
  'cmd': 'Meta',
  'win': 'Meta',
};

const KEY_MAP: Record<string, string> = {
  'enter': 'Enter', 'escape': 'Escape', 'space': 'Space',
  'tab': 'Tab', 'backspace': 'Backspace', 'delete': 'Delete',
  'up': 'UpArrow', 'down': 'DownArrow', 'left': 'LeftArrow', 'right': 'RightArrow',
  'home': 'Home', 'end': 'End', 'pageup': 'PageUp', 'pagedown': 'PageDown',
  'f1': 'F1', 'f2': 'F2', 'f3': 'F3', 'f4': 'F4', 'f5': 'F5', 'f6': 'F6',
  'f7': 'F7', 'f8': 'F8', 'f9': 'F9', 'f10': 'F10', 'f11': 'F11', 'f12': 'F12',
};

export function keybindingToMonaco(keybinding: KeybindingData, platform?: string): string {
  const parts = keybinding.key.split('+').map(p => p.trim().toLowerCase());
  const monacoKeys: string[] = [];
  for (const part of parts) {
    if (MODIFIER_MAP[part]) {
      monacoKeys.push(MODIFIER_MAP[part]!);
    } else if (KEY_MAP[part]) {
      monacoKeys.push(KEY_MAP[part]!);
    } else if (part.length === 1) {
      monacoKeys.push('Key' + part.toUpperCase());
    } else {
      monacoKeys.push(part);
    }
  }
  return monacoKeys.join(' | ');
}

export function keybindingFromMonaco(monacoKeybinding: string): string {
  const parts = monacoKeybinding.split(' | ');
  const keys: string[] = [];
  for (const part of parts) {
    const lower = part.toLowerCase();
    if (lower === 'ctrl') keys.push('ctrl');
    else if (lower === 'shift') keys.push('shift');
    else if (lower === 'alt') keys.push('alt');
    else if (lower === 'meta') keys.push('cmd');
    else if (lower.startsWith('key')) keys.push(lower.slice(3));
    else {
      for (const [k, v] of Object.entries(KEY_MAP)) {
        if (v.toLowerCase() === lower) { keys.push(k); break; }
      }
      if (keys.length < parts.indexOf(part) + 1) keys.push(part.toLowerCase());
    }
  }
  return keys.join('+');
}

export function registerCommandsInMonaco(
  commands: CommandData[],
): MonacoCommandMapping[] {
  return commands.map(cmd => {
    const monacoCmd = BUILTIN_COMMAND_MAP.get(cmd.id) ?? cmd.id;
    return {
      editorCommand: cmd.id,
      monacoCommand: monacoCmd,
      bidirectional: BUILTIN_COMMAND_MAP.has(cmd.id),
    };
  });
}

// ═══ §7 EVENT MAPPING — editor events ↔ Monaco events ═══

export const MODEL_EVENTS: readonly string[] = [
  'onDidChangeContent',
  'onDidChangeDecorations',
  'onDidChangeOptions',
  'onDidChangeLanguage',
  'onDidChangeLanguageConfiguration',
  'onWillDispose',
] as const;

export const EDITOR_EVENTS: readonly string[] = [
  'onDidChangeCursorPosition',
  'onDidChangeCursorSelection',
  'onDidContentSizeChange',
  'onDidScrollChange',
  'onDidFocusEditorText',
  'onDidBlurEditorText',
  'onDidFocusEditorWidget',
  'onDidBlurEditorWidget',
  'onKeyDown',
  'onKeyUp',
  'onDidPaste',
  'onDidLayoutChange',
  'onMouseDown',
  'onMouseUp',
  'onMouseMove',
  'onDidAttemptReadOnlyEdit',
] as const;

const EVENT_TYPE_MAP: ReadonlyMap<string, string> = new Map([
  ['document.changed', 'onDidChangeContent'],
  ['cursor.changed', 'onDidChangeCursorPosition'],
  ['cursor.updated', 'onDidChangeCursorSelection'],
  ['editor.changed', 'onDidContentSizeChange'],
  ['layout.changed', 'onDidScrollChange'],
  ['editor.activated', 'onDidFocusEditorText'],
  ['editor.deactivated', 'onDidBlurEditorText'],
  ['command.requested', 'onKeyDown'],
]);

const MONACO_EVENT_REVERSE: ReadonlyMap<string, string> = new Map(
  Array.from(EVENT_TYPE_MAP.entries()).map(([k, v]) => [v, k]),
);

export function mapEventType(type: string): string {
  return EVENT_TYPE_MAP.get(type) ?? MONACO_EVENT_REVERSE.get(type) ?? type;
}

export function editorEventToMonaco(event: EditorEventData): Record<string, unknown> {
  return {
    type: mapEventType(event.type),
    payload: event.payload,
    timestamp: event.timestamp,
    source: event.source,
  };
}

export function monacoEventToEditorEvent(monacoEvent: Record<string, unknown>): Partial<EditorEventData> {
  const monacoType = monacoEvent['type'] as string;
  return {
    channel: asEventChannelId('monaco'),
    type: mapEventType(monacoType),
    payload: monacoEvent['payload'] ?? monacoEvent,
    timestamp: (monacoEvent['timestamp'] as number) ?? now(),
    source: 'monaco',
    priority: 'normal',
  };
}

// ═══ §8 TOKEN MAPPING — tokens, semantic tokens, language IDs ═══

export const LANGUAGE_ID_MAP: ReadonlyMap<string, string> = new Map([
  ['typescript', 'typescript'],
  ['javascript', 'javascript'],
  ['typescriptreact', 'typescriptreact'],
  ['javascriptreact', 'javascriptreact'],
  ['json', 'json'],
  ['jsonc', 'json'],
  ['html', 'html'],
  ['css', 'css'],
  ['scss', 'scss'],
  ['less', 'less'],
  ['markdown', 'markdown'],
  ['python', 'python'],
  ['rust', 'rust'],
  ['go', 'go'],
  ['java', 'java'],
  ['c', 'c'],
  ['cpp', 'cpp'],
  ['csharp', 'csharp'],
  ['ruby', 'ruby'],
  ['php', 'php'],
  ['swift', 'swift'],
  ['kotlin', 'kotlin'],
  ['yaml', 'yaml'],
  ['toml', 'toml'],
  ['xml', 'xml'],
  ['sql', 'sql'],
  ['shell', 'shell'],
  ['bash', 'shell'],
  ['powershell', 'powershell'],
  ['dockerfile', 'dockerfile'],
  ['graphql', 'graphql'],
  ['lua', 'lua'],
  ['r', 'r'],
  ['perl', 'perl'],
  ['scala', 'scala'],
  ['elixir', 'elixir'],
  ['haskell', 'haskell'],
  ['clojure', 'clojure'],
  ['dart', 'dart'],
  ['svelte', 'svelte'],
  ['vue', 'vue'],
]);

export function languageIdToMonaco(languageId: string): string {
  return LANGUAGE_ID_MAP.get(languageId) ?? languageId;
}

export function languageIdFromMonaco(monacoLanguageId: string): string {
  for (const [k, v] of LANGUAGE_ID_MAP) {
    if (v === monacoLanguageId) return k;
  }
  return monacoLanguageId;
}

const TOKEN_TYPE_TO_SCOPE: ReadonlyMap<TokenType, string> = new Map([
  ['keyword', 'keyword'],
  ['identifier', 'identifier'],
  ['string', 'string'],
  ['number', 'number'],
  ['operator', 'operator'],
  ['comment', 'comment'],
  ['punctuation', 'delimiter'],
  ['type', 'type'],
  ['decorator', 'annotation'],
  ['regex', 'regexp'],
  ['template', 'string.template'],
  ['embedded', 'metatag'],
  ['whitespace', ''],
  ['invalid', 'invalid'],
]);

const SCOPE_TO_TOKEN_TYPE: ReadonlyMap<string, TokenType> = new Map(
  Array.from(TOKEN_TYPE_TO_SCOPE.entries())
    .filter(([, v]) => v !== '')
    .map(([k, v]) => [v, k]),
);

export function tokenToMonaco(token: Token): Record<string, unknown> {
  return {
    startIndex: token.startOffset,
    scopes: TOKEN_TYPE_TO_SCOPE.get(token.type) ?? token.type,
  };
}

export function tokenFromMonaco(monacoToken: Record<string, unknown>): Partial<Token> {
  const scopes = monacoToken['scopes'] as string;
  const baseScope = scopes.split('.')[0] ?? scopes;
  return {
    type: SCOPE_TO_TOKEN_TYPE.get(baseScope) ?? SCOPE_TO_TOKEN_TYPE.get(scopes) ?? 'identifier',
    startOffset: monacoToken['startIndex'] as number,
    endOffset: (monacoToken['endIndex'] as number | undefined) ?? (monacoToken['startIndex'] as number),
    scopes: [scopes],
  };
}

export function semanticTokensToMonaco(tokens: SemanticToken[]): Record<string, unknown> {
  const data: number[] = [];
  let prevLine = 0;
  let prevChar = 0;
  for (const token of tokens) {
    const deltaLine = token.line - prevLine;
    const deltaChar = deltaLine === 0 ? token.startChar - prevChar : token.startChar;
    data.push(deltaLine, deltaChar, token.length, 0, 0);
    prevLine = token.line;
    prevChar = token.startChar;
  }
  return { data };
}

export function monacoThemeRuleToThemeRule(rule: Record<string, unknown>): Record<string, unknown> {
  return {
    token: rule['token'] as string,
    foreground: rule['foreground'] as string | undefined,
    background: rule['background'] as string | undefined,
    fontStyle: rule['fontStyle'] as string | undefined,
  };
}
